import React, { useState, useEffect, useRef } from "react";

const FAQ_DATA = [
  {
    q: "What is Diviseed?",
    a: `Diviseed is a data-driven tool for Turo hosts, investors, and fleet operators. It helps you identify the best-performing vehicles in your market based on real booking data, so you can make smarter purchasing and pricing decisions.`,
  },
  {
    q: "How accurate are the revenue estimates?",
    a: `Revenue estimates are based on previous booking data from Turo, their listed prices, and user provided data. They do not include earnings from fees or add-ons. While no projection is perfect, our methodology is designed to give you a clear baseline for comparison.`,
  },
  {
    q: "How often is the data updated?",
    a: `We continuously add the latest Turo listings to our database, prioritizing high-demand markets and trending search queries. The database grows based on user searches and market demand, so results get smarter and more comprehensive over time.`,
  },
  {
    q: "Where does the data come from?",
    a: `We use publicly available data from Turo and other vetted sources to generate comprehensive analytics around vehicle performance, demand, and ROI potential.`,
  },
  {
    q: "Can I request data for a specific market?",
    a: `Yes. If you don’t see the data you need, run searches in your market—high-demand searches influence which listings we prioritize for updates. If you have something specific, let us know too in "Contact Us"`,
  },
];

function AccordionItem({ i, question, answer, isOpen, onToggle }) {
  const panelId = `faq-panel-${i}`;
  const buttonId = `faq-button-${i}`;
  return (
    <div className="border-b">
      <button
        id={buttonId}
        aria-controls={panelId}
        aria-expanded={isOpen}
        onClick={onToggle}
        className="w-full flex items-center justify-between py-3 text-left"
      >
        <span className="font-medium text-gray-900">{question}</span>
        <svg
          className={`h-5 w-5 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.04 1.08l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.31a.75.75 0 01.02-1.1z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className={`grid transition-all duration-200 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="pb-4 text-sm text-gray-700">{answer}</p>
        </div>
      </div>
    </div>
  );
}

function FAQAccordion({ defaultOpenIndex = 0 }) {
  const [openIndex, setOpenIndex] = useState(defaultOpenIndex);
  return (
    <div className="divide-y">
      {FAQ_DATA.map((item, i) => (
        <AccordionItem
          key={i}
          i={i}
          question={item.q}
          answer={item.a}
          isOpen={openIndex === i}
          onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
        />
      ))}
    </div>
  );
}

export default function FAQPopover() {
  const [showFAQ, setShowFAQ] = useState(false);
  const dialogRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setShowFAQ(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close on outside click
  useEffect(() => {
    function onClick(e) {
      if (
        showFAQ &&
        dialogRef.current &&
        !dialogRef.current.contains(e.target)
      ) {
        setShowFAQ(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showFAQ]);

  return (
    <div className="w-full text-center p-4 text-sm relative">
      <button
        onClick={() => setShowFAQ(!showFAQ)}
        className="text-blue-600 underline"
      >
        Frequently Asked Questions
      </button>

      {showFAQ && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 z-40" />
          {/* Popover */}
          <div className="fixed left-1/2 top-16 -translate-x-1/2 z-50 w-[92vw] max-w-2xl">
            <div
              ref={dialogRef}
              className="relative bg-white shadow-lg border rounded p-6 text-left overflow-y-auto max-h-[80vh]"
            >
              <button
                onClick={() => setShowFAQ(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-black text-lg"
                aria-label="Close FAQ"
              >
                ×
              </button>

              <h2 className="text-2xl font-bold mb-4">
                Frequently Asked Questions
              </h2>
              <FAQAccordion />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
